'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function GozAltiIsikDolgusunaUygunluk() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/goz-alti-isik-dolgusuna-uygunluk-kimler-icin-ideal-kimler-icin-degil';
  const pageTitle = 'Göz Altı Işık Dolgusuna Uygunluk: Kimler İçin İdeal, Kimler İçin Değil?';
  const pageDescription = 'Göz altı ışık dolgusu herkese yapılabilir mi? Uygun aday kriterleri, riskli durumlar ve dikkat edilmesi gereken noktalar.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Göz Altı Işık Dolgusuna Uygunluk', href: '/tr/blog/goz-alti-isik-dolgusuna-uygunluk-kimler-icin-ideal-kimler-icin-degil' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Göz altı ışık dolgusu uygunluk kriterleri"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Göz Altı Estetiği
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Göz Altı Işık Dolgusuna Uygunluk: Kimler İçin İdeal, Kimler İçin Değil?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>26 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Göz altı bölgesi, yüzün en hassas ve en ince derili kısmıdır.
                Yorgunluk, uykusuzluk, stres ve yaşla birlikte bu bölge çöker, morlaşır ve mat bir görünüm alır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Işık dolgusu, bu alandaki gölgelenmeyi azaltmak ve daha aydınlık bir ifade kazandırmak için geliştirilmiştir.
                Ancak her kişi bu işleme uygun aday değildir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Işık Dolgusu Nedir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz altına uygulanan özel hyaluronik asit bazlı bir dolgu türüdür.
              </p>

              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <p className="text-indigo-800 mb-4 font-medium">
                  Klasik dolgulardan farkı:
                </p>
                <ul className="list-disc list-inside space-y-2 text-indigo-800">
                  <li>Daha ince moleküler yapıdadır.</li>
                  <li>Cilt altına homojen dağılır.</li>
                  <li>Doğal parlaklık etkisi yaratır.</li>
                </ul>
                <div className="bg-indigo-100 rounded-lg p-4 mt-4">
                  <p className="text-indigo-900 font-medium">
                    En çok kullanılan ürün: Teosyal Redensity II — göz altı bölgesi için özel formüle edilmiştir.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Uygun Aday Kriterleri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">✅ Göz altı çukuru mevcutsa</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu boşluğu doldurarak aydınlık sağlar.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">✅ Hafif morluk varsa</td>
                      <td className="border border-gray-300 px-4 py-3">Kan dolaşımını düzenleyerek rengi açar.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">✅ Göz altı torbası hafifse</td>
                      <td className="border border-gray-300 px-4 py-3">Optik olarak daha düz görünüm sağlar.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">✅ Cilt elastikiyeti yerindeyse</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu düzgün dağılır, pürüzsüz sonuç verir.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygun Olmayan Durumlar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Neden Riskli</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">❌ Belirgin yağ torbası</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu şişkinliği artırabilir.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">❌ Cilt çok ince ve damarlıysa</td>
                      <td className="border border-gray-300 px-4 py-3">Morarma riski artar.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">❌ Kronik ödem veya alerji varsa</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu tutunması zayıf olur.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">❌ Tiroid veya böbrek hastalığı</td>
                      <td className="border border-gray-300 px-4 py-3">Göz çevresi ödem eğilimli olur.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  💬 Işık dolgusu, doğru hastada mucizevi etki; yanlış hastada hayal kırıklığı yaratabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Uygulama Süreci
              </h2>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside space-y-3 text-blue-800">
                  <li>Cilt antiseptikle temizlenir.</li>
                  <li>Lokal anestezik krem sürülür.</li>
                  <li>Mikro kanül yardımıyla dolgu, göz altına kemik hattı boyunca uygulanır.</li>
                  <li>İşlem 15–20 dakika sürer.</li>
                </ol>
                <div className="bg-blue-100 rounded-lg p-4 mt-4">
                  <p className="text-blue-900 font-medium">
                    Sonuçlar anında görünür, ancak tam oturma süresi 1 haftadır.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Kalıcılık Süresi
              </h2>
              
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <div className="flex items-center mb-4">
                  <span className="text-purple-600 text-2xl mr-3">📅</span>
                  <span className="text-purple-800 font-medium">Ortalama 12–18 ay.</span>
                </div>
                <p className="text-purple-800">
                  Kullanılan ürün ve cilt yapısına göre değişir.
                </p>
                <p className="text-purple-800">
                  Tekrarlayan uygulamalarda etki süresi uzar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Sonrası Bakım
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>İlk 24 saat soğuk kompres yapılabilir.</li>
                  <li>48 saat makyajdan kaçınılmalı.</li>
                  <li>1 hafta sauna, hamam yasak.</li>
                  <li>Bol su tüketimi dolgunun tutunmasını destekler.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Alternatif Tedavi Yöntemleri
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-yellow-50 rounded-xl p-6">
                  <h4 className="font-semibold text-yellow-900 mb-4 flex items-center">
                    <span className="text-yellow-600 mr-2">⚡</span>
                    Cerrahi Olmayan Alternatifler
                  </h4>
                  <ul className="space-y-2 text-yellow-800">
                    <li>• PRP (Platelet Rich Plasma)</li>
                    <li>• Mezoterapi</li>
                    <li>• LED ışık terapisi</li>
                    <li>• Kollajen stimülasyonu</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 rounded-xl p-6">
                  <h4 className="font-semibold text-red-900 mb-4 flex items-center">
                    <span className="text-red-600 mr-2">🔪</span>
                    Cerrahi Alternatifler
                  </h4>
                  <ul className="space-y-2 text-red-800">
                    <li>• Blefaroplasti (göz kapağı estetiği)</li>
                    <li>• Yağ transferi</li>
                    <li>• Alt göz kapağı lifting</li>
                    <li>• Kombine cerrahi işlemler</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8. Fiyat Bilgisi
              </h2>
              
              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <div className="text-center">
                  <h4 className="font-semibold text-indigo-900 mb-2">Göz Altı Işık Dolgusu</h4>
                  <p className="text-3xl font-bold text-indigo-800 mb-2">₺3.500 - ₺4.500</p>
                  <p className="text-indigo-700 text-sm">Teosyal Redensity II ile</p>
                  <p className="text-indigo-700 text-sm mt-2">
                    * Fiyatlar 2026 yılı için geçerlidir. Detaylı fiyat bilgisi için iletişime geçin.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Işık dolgusu, doğru hastada adeta 8 saat uyumuş etkisi yaratır.
                  Ancak her göz altı torbası dolguya uygun değildir; bazen cerrahi blefaroplasti daha iyi sonuç verir."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Göz altımda morluk var ama torba yok, yapılabilir mi?</h3>
                  <p className="text-gray-600">Evet, bu durumda ışık dolgusu çok etkilidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Şişlik yapar mı?</h3>
                  <p className="text-gray-600">Doğru ürün ve teknikle yapılırsa şişlik geçicidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Kalıcılığı ne kadardır?</h3>
                  <p className="text-gray-600">Yaklaşık 12–18 ay.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Göz altına klasik dolgu yapılabilir mi?</h3>
                  <p className="text-gray-600">Hayır, bu bölge için özel olarak üretilmiş ışık dolgusu kullanılmalıdır.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Ağrılı bir işlem midir?</h3>
                  <p className="text-gray-600">Hayır, anestezik kremle birlikte rahatsızlık hissi minimumdur.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Hangi yaşlarda yapılabilir?</h3>
                  <p className="text-gray-600">25 yaş üzeri herkese uygulanabilir, ancak 30+ yaşlarda daha etkilidir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS Global Report (2024) – "Tear Trough Fillers and Indications"</li>
                  <li>Teoxane Laboratories – "Teosyal Redensity II Clinical Data"</li>
                  <li>Harvard Health (2023) – "Under Eye Treatments: Safety and Efficacy"</li>
                  <li>TETD (2024) – "Göz Altı Dolgusu Uygulama Kriterleri"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#gözaltı</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#ışıkdolgu</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#teosyal</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#gözaltımorluk</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#blefaroplasti</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Göz Altı Işık Dolgusu Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan göz altı ışık dolgusu uygunluğu hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Göz altı ışık dolgusu hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Teknikleri</h4>
              <p className="text-gray-600 text-sm">Russian Lips, Paris Lips, Volume Lips gibi tekniklerin farkları ve uygulama süreci.</p>
            </a>
            <a
              href="/tr/blog/en-iyi-dudak-dolgusu-markasi-dogallik-kalicilik-ve-guven"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">En İyi Dudak Dolgusu Markası</h4>
              <p className="text-gray-600 text-sm">Kalıcılığı, doğallığı, FDA onayı ve güvenilirliğiyle öne çıkan dudak dolgularının karşılaştırması.</p>
            </a>
            <a
              href="/tr/blog/alin-bolgesine-dolgu-uygulamasi-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Alın Bölgesine Dolgu Uygulaması</h4>
              <p className="text-gray-600 text-sm">Alın bölgesine dolgu uygulaması nasıl yapılır? Etkileri ve uygulama süreci.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
